import React, { useContext, useEffect, useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { H4, Separator, Paragraph, Image, usePageWidth } from '@fs/zion-ui'
import { NoticeLoading } from '@fs/zion-icon'
import GlobalContext from '../context/globalContext'
import type { GlobalContextType } from '../context/types'
import { getAllApplicationsFromDB } from '../shared/services'
import errorImage from '../shared/images/applicationsOverviewError.png'
import AppShell from './AppShell'

const centeredStyles = css`
  width: 100%;
  min-height: 60vh;
  display: grid;
  place-items: center;
  padding: 35px 20px;
`

/**
 * Ensures the user's applications are fetched before rendering any route that
 * depends on them. Every application-backed page (overview, environments,
 * details) can be entered directly via a deep link or hard refresh, at which
 * point the global applications Map may not be hydrated yet. Centralizing the
 * fetch here means those pages no longer each re-implement it, and it prevents
 * a valid application from momentarily rendering as "not found" while the
 * initial fetch is still in flight.
 */
export default function ApplicationsGate({ children }: { children: React.ReactNode }): JSX.Element {
  const {
    applications,
    setAllApplications,
    fetchApplicationsAgain,
    hasFetchedApplications,
    setHasFetchedApplications,
    resetFetchApplicationsAgain,
  } = useContext(GlobalContext) as GlobalContextType
  const atSize = usePageWidth()
  const [t] = useTranslation()
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const fetchAllUserApplications = async (): Promise<void> => {
      try {
        setHasError(false)
        const res = await getAllApplicationsFromDB()
        if (res?.status === 200) {
          setAllApplications(res?.data)
        }
      } catch (error) {
        console.error('Error fetching user applications:', error)
        setHasError(true)
      } finally {
        // Always mark the fetch as attempted so downstream pages can make a
        // reliable found / not-found decision instead of waiting forever.
        setHasFetchedApplications(true)
        if (fetchApplicationsAgain) {
          resetFetchApplicationsAgain()
        }
      }
    }

    if ((applications?.size === 0 && hasFetchedApplications === false) || fetchApplicationsAgain) {
      fetchAllUserApplications()
    } else {
      setHasFetchedApplications(true)
    }
  }, [
    applications?.size,
    setAllApplications,
    hasFetchedApplications,
    setHasFetchedApplications,
    fetchApplicationsAgain,
    resetFetchApplicationsAgain,
  ])

  // Render the loading and error states inside AppShell so its account fetch
  // and onboarding redirect still run even when the applications fetch never
  // succeeds — otherwise a non-onboarded user (whose applications fetch fails)
  // would be stuck on the error state instead of being sent to /onboarding.
  if (!hasFetchedApplications) {
    return (
      <AppShell>
        <div css={centeredStyles}>
          <NoticeLoading size="lg" />
        </div>
      </AppShell>
    )
  }

  if (hasError) {
    return (
      <AppShell>
        <div css={centeredStyles}>
          <div style={{ maxWidth: '670px', margin: '0 auto', textAlign: 'center' }}>
            <Image src={errorImage} alt="Error Fetching Applications" width="auto" />
            <H4 size={atSize({ default: 'H4', sm: 'H3' })}>
              {t('overview.fetch-error', 'We were unable to load this page')}
            </H4>
            <Separator size="xxs" />
            <Paragraph size={atSize({ default: 'xs', sm: 'sm' })}>
              {t(
                'overview.fetch-error-message',
                'We couldn’t load your applications due to a system error. Please try again. If the problem persists please reach out to devsupport@familysearch.org'
              )}
            </Paragraph>
          </div>
        </div>
      </AppShell>
    )
  }

  return <>{children}</>
}
